import { shouldHideUnifiedSpeedDial } from '../../utils/speedDialVisibility';

export type LearningLabNavState =
  | 'hidden'
  | 'loading'
  | 'statusUnavailable'
  | 'serverDisabled'
  | 'unprovisioned'
  | 'impersonationBlocked'
  | 'available'
  | 'active'
  | 'activeStale';

export type LearningLabNavContext = {
  isAuthenticated: boolean;
  isCoordinator: boolean;
  featureEnabled: boolean;
  serverEnabled: boolean;
  provisioned: boolean;
  active: boolean;
  isStale: boolean;
  loading: boolean;
  /** True when GET .../learning-lab/status failed or returned unusable payload. */
  statusUnavailable: boolean;
  isImpersonating: boolean;
  pathname: string;
  subdomain: string;
};

export function shouldShowLearningLabNav(
  pathname: string,
  subdomain: string,
): boolean {
  return !shouldHideUnifiedSpeedDial(pathname, subdomain);
}

export function resolveLearningLabNavState(
  ctx: LearningLabNavContext,
): LearningLabNavState {
  if (!ctx.isAuthenticated) {
    return 'hidden';
  }
  if (!ctx.isCoordinator || !ctx.featureEnabled) {
    return 'hidden';
  }
  if (!shouldShowLearningLabNav(ctx.pathname, ctx.subdomain)) {
    return 'hidden';
  }
  if (ctx.loading) {
    return 'loading';
  }
  if (ctx.statusUnavailable) {
    return 'statusUnavailable';
  }
  if (!ctx.serverEnabled) {
    return 'serverDisabled';
  }
  if (!ctx.provisioned) {
    return 'unprovisioned';
  }
  if (ctx.isImpersonating && !ctx.active) {
    return 'impersonationBlocked';
  }
  if (ctx.active && ctx.isStale) {
    return 'activeStale';
  }
  if (ctx.active) {
    return 'active';
  }
  return 'available';
}

export function isLearningLabNavIconVisible(state: LearningLabNavState): boolean {
  return state !== 'hidden';
}

export function isLearningLabNavIconDisabled(state: LearningLabNavState): boolean {
  return (
    state === 'loading' ||
    state === 'statusUnavailable' ||
    state === 'serverDisabled' ||
    state === 'unprovisioned' ||
    state === 'impersonationBlocked'
  );
}

export function isLearningLabNavIconActive(state: LearningLabNavState): boolean {
  return state === 'active' || state === 'activeStale';
}

export function getLearningLabNavAriaLabel(state: LearningLabNavState): string {
  switch (state) {
    case 'hidden':
      return 'Learning Lab';
    case 'loading':
      return 'Learning Lab, loading';
    case 'statusUnavailable':
      return 'Learning Lab, status unavailable';
    case 'serverDisabled':
    case 'unprovisioned':
      return 'Learning Lab unavailable';
    case 'impersonationBlocked':
      return 'Learning Lab, exit impersonation first';
    case 'active':
      return 'Learning Lab, active';
    case 'activeStale':
      return 'Learning Lab, active, content updated';
    case 'available':
    default:
      return 'Learning Lab';
  }
}

export function getLearningLabNavTooltip(state: LearningLabNavState): string | null {
  switch (state) {
    case 'loading':
      return 'Checking Learning Lab availability…';
    case 'statusUnavailable':
      return 'Could not load Learning Lab status';
    case 'serverDisabled':
      return 'Learning Lab is not enabled on the server';
    case 'unprovisioned':
      return 'Learning Lab is not set up yet';
    case 'impersonationBlocked':
      return 'Exit impersonation to enter the Learning Lab';
    default:
      return null;
  }
}

export function getLearningLabNavStatusMessage(state: LearningLabNavState): string | null {
  switch (state) {
    case 'serverDisabled':
      return 'Learning Lab is not enabled on this environment.';
    case 'statusUnavailable':
      return 'Could not load Learning Lab status. Sign in as a coordinator, ensure `pnpm run dev:server` is running, and set COORDINATOR_LEARNING_LAB_ENABLED=true in dev.env (then restart API + Vite).';
    case 'unprovisioned':
      return 'Learning Lab is not set up yet. Contact your administrator.';
    case 'impersonationBlocked':
      return 'Exit impersonation before entering the Learning Lab.';
    case 'active':
      return "You're in the Learning Lab";
    case 'activeStale':
      return 'Lab content was updated — exit and re-enter to see the latest sample data.';
    default:
      return null;
  }
}

/** Matches OnboardingMenu lab section visibility (fully eligible coordinator). */
export function shouldShowLearningLabMenuSection(
  ctx: LearningLabNavContext,
): boolean {
  return resolveLearningLabNavState(ctx) === 'available' ||
    resolveLearningLabNavState(ctx) === 'active' ||
    resolveLearningLabNavState(ctx) === 'activeStale';
}

export const LEARNING_LAB_DASHBOARD_PROMPT_DISMISSED_KEY =
  'apt.learningLab.dashboardPromptDismissed';

export function isLearningLabDashboardPromptDismissed(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(LEARNING_LAB_DASHBOARD_PROMPT_DISMISSED_KEY) === 'v1';
  } catch {
    return false;
  }
}

export function dismissLearningLabDashboardPrompt(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(LEARNING_LAB_DASHBOARD_PROMPT_DISMISSED_KEY, 'v1');
  } catch {
    // ignore quota / private mode
  }
}
